import React, { useContext } from 'react';
import ChatConnectionContext from '../../contexts/ChatConnectionContext';
import { WifiOff, Wifi, AlertCircle, RefreshCw, HelpCircle, Loader2 } from 'lucide-react';
import './ConnectionStatusIndicator.css';

interface ConnectionStatusIndicatorProps {
  className?: string;
  showText?: boolean;
  position?: 'top' | 'inline';
  activeConversationRef?: string | number;
}

const ConnectionIcon: React.FC<{ 
  status: string; 
  className?: string; 
  animate?: boolean;
}> = ({ status, className = '', animate = false }) => {
  const baseClasses = `connection-icon ${className} ${animate ? 'animate' : ''}`;
  
  switch (status) {
    case 'disconnected':
      return <WifiOff aria-hidden className={`${baseClasses} disconnected`} />;
    case 'connecting':
      return <Loader2 aria-hidden className={`${baseClasses} connecting`} />;
    case 'connected':
      return <Wifi aria-hidden className={`${baseClasses} connected`} />;
    case 'error':
      return <AlertCircle aria-hidden className={`${baseClasses} error`} />;
    case 'reconnecting':
      return <RefreshCw aria-hidden className={`${baseClasses} reconnecting`} />;
    default:
      return <HelpCircle aria-hidden className={`${baseClasses} unknown`} />;
  }
};

const ConnectionStatusIndicator: React.FC<ConnectionStatusIndicatorProps> = ({ 
  className = '',
  showText = true,
  position = 'top',
  activeConversationRef
}) => {
  // Use context directly and handle undefined case
  const context = useContext(ChatConnectionContext);
  
  // If context is not available yet, return null or minimal UI
  if (!context) {
    console.debug('[ConnectionStatusIndicator] Context not ready yet');
    return null;
  }
  
  const { connectionStatus, isConnected, isConnecting, hasError } = context;
  
  // Only show top-level indicator when an error has occurred; suppress connecting/ready/disconnected states
  if (position === 'top' && !hasError) {
    return null;
  }
  
  const getStatusText = () => {
    // Only surface errors; otherwise neutral
    if (!hasError) return '';
    return 'Connection failed';
  };
  
  const getStatusClass = () => {
    const USE_SSE_CHAT = (import.meta as any)?.env?.VITE_USE_SSE_CHAT === 'true';
    if (USE_SSE_CHAT) {
      switch (connectionStatus) {
        case 'error':
          return 'status-error';
        case 'connecting':
        case 'reconnecting':
          return 'status-connecting';
        default:
          return 'status-connected';
      }
    }
    switch (connectionStatus) {
      case 'connecting':
      case 'reconnecting':
        return 'status-connecting';
      case 'connected':
        return 'status-connected';
      case 'disconnected':
        return 'status-disconnected';
      case 'error':
        return 'status-error';
      default:
        return 'status-unknown';
    }
  };
  
  const containerClasses = `
    connection-status-indicator 
    ${getStatusClass()} 
    ${position} 
    ${className}
  `.trim();
  
  return (
    <div className={containerClasses} data-conversation-ref={activeConversationRef ?? ''}>
      <div className="status-content">
        <ConnectionIcon 
          status={connectionStatus} 
          animate={isConnecting || connectionStatus === 'reconnecting'}
          className="status-icon"
        />
        {showText && (
          <span className="status-text">
            {getStatusText()}
          </span>
        )}
      </div>
    </div>
  );
};

export default ConnectionStatusIndicator;